import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Award, CheckCircle, Zap, Users } from "lucide-react";

const stats = [
  { icon: Award, value: 10, suffix: "+", label: "Años de experiencia", desc: "Trayectoria comprobada" },
  { icon: CheckCircle, value: 500, suffix: "+", label: "Proyectos completados", desc: "En todo el AMBA" },
  { icon: Zap, value: 3000, suffix: "km", label: "De fibra tendida", desc: "Red de alta performance" },
  { icon: Users, value: 99, suffix: "%", label: "Clientes satisfechos", desc: "Calidad garantizada" },
];

const useCounter = (target, duration, inView) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    setCount(0);
    let start = 0;
    const steps = 60;
    const increment = target / steps;
    const interval = duration / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, interval);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
};

const StatItem = ({ stat, index, inView }) => {
  const Icon = stat.icon;
  const count = useCounter(stat.value, 1800, inView);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      data-testid={`stat-${index}`}
      className="text-center group"
    >
      <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-accent transition-colors duration-300 group-hover:scale-110 transform transition-transform">
        <Icon size={28} className="text-accent group-hover:text-white transition-colors duration-300" />
      </div>
      <div className="font-heading font-black text-5xl md:text-6xl text-white mb-1 animate-counter-glow">
        {count.toLocaleString()}<span className="text-accent">{stat.suffix}</span>
      </div>
      <div className="font-heading font-bold text-white/90 text-lg mb-1">{stat.label}</div>
      <div className="font-body text-white/50 text-sm">{stat.desc}</div>
    </motion.div>
  );
};

export const Stats = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="stats-section"
      className="py-24 md:py-28 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #003366 0%, #1A1A2E 100%)" }}
    >
      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/40 rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-accent font-heading font-semibold text-sm tracking-widest uppercase block mb-3">
            Nuestros números
          </span>
          <h2 className="font-heading font-black text-white text-4xl md:text-5xl">
            Resultados que hablan
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {stats.map((stat, i) => (
            <StatItem key={stat.label} stat={stat} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};
